<template>
  <section>
    <div class="flex justify-between items-center">
      <h2 class="page-title">Search for your favorite Au Pairs</h2>

      <button
        @click.prevent="toggleFilters()"
        class="btn btn-purple cursor-pointer"
      >
        Filters
        <i class="material-icons align-middle text-4xl text-white">notes</i>
      </button>
    </div>
    <div id="side-filters" :class="[isFilterOpen ? 'opened' : 'closed']">
      <div class="wrapper">
        <button class="close-filter" @click.prevent="toggleFilters()">
          <i class="material-icons">close</i>
        </button>
        <div class="filtering px-5">
          <div class="flex flex-col justify-between items-center mt-5">
            <!-- nationality -->
            <div class="nationality mb-4 w-full">
              <h3>
                Nationality
              </h3>
              <select
                v-model="birthCountry"
                v-if="availableCountries"
                class="block appearance-none w-full bg-white border border-gray-400 hover:border-gray-500 px-4 py-2 pr-8 rounded leading-tight focus:outline-none focus:shadow-outline"
              >
                <option value="all" selected>Show All</option>
                <option
                  v-for="(country, index) in availableCountries"
                  :key="index"
                  :value="country.name"
                  >{{ country.name }}</option
                >
              </select>
              <ComponentLoading v-else />
            </div>

            <!-- name -->
            <div class="nationality mb-4 w-full">
              <h3>
                First Name
              </h3>
              <input
                type="text"
                class="block appearance-none w-full bg-white border border-gray-400 hover:border-gray-500 px-4 py-2 pr-8 rounded leading-tight focus:outline-none focus:shadow-outline"
                v-model="name"
              />
            </div>

            <!-- age range -->
            <div class="age mb-4 w-full border-b border-purple-ap-t03 pb-5">
              <h3>
                Age
              </h3>

              <div class="mb-5">
                <p>
                  From
                  <strong>{{ ageFrom }}</strong>
                </p>

                <div class="slidecontainer">
                  <input
                    @change="adjustTo"
                    v-model="ageFrom"
                    type="range"
                    min="18"
                    max="27"
                    value="18"
                    class="slider"
                    id="ageFrom"
                  />
                </div>
              </div>

              <p>
                Up to
                <strong>{{ ageTo }}</strong>
              </p>
              <div class="slidecontainer">
                <input
                  @change="adjustFrom"
                  v-model="ageTo"
                  type="range"
                  min="18"
                  max="27"
                  value="27"
                  class="slider"
                  id="ageTo"
                />
              </div>
            </div>

            <!-- infant care qualified -->
            <div class="infant mb-4 w-full border-b border-purple-ap-t03 pb-5">
              <h3 class="mb-0">
                Infant Care Required
              </h3>
              <p class="mb-3">
                <small
                  >Infant Care is required for children under the age of two (2)
                  years old.</small
                >
              </p>
              <div class="radio-toolbar">
                <input
                  v-model="infantCareQualified"
                  type="radio"
                  id="radioInfantAll"
                  name="infantCare"
                  value="all"
                  checked
                />
                <label for="radioInfantAll">Show all</label>

                <input
                  v-model="infantCareQualified"
                  type="radio"
                  id="radioInfantYes"
                  name="infantCare"
                  :value="true"
                />
                <label for="radioInfantYes">Yes</label>
              </div>
            </div>

            <!-- driving -->
            <div class="driving mb-4 w-full border-b border-purple-ap-t03 pb-5">
              <h3>
                Driving Skills
              </h3>
              <div class="radio-toolbar wrapped">
                <div>
                  <input
                    v-model="experiencedDriver"
                    type="radio"
                    id="radioDrivingAll"
                    name="driving"
                    value="all"
                    checked
                  />
                  <label for="radioDrivingAll">Show All</label>
                </div>
                <div>
                  <input
                    v-model="experiencedDriver"
                    type="radio"
                    id="radioDrivingBeginner"
                    name="driving"
                    :value="1"
                  />
                  <label for="radioDrivingBeginner">Beginner</label>
                </div>
                <div>
                  <input
                    v-model="experiencedDriver"
                    type="radio"
                    id="radioDrivingIntermediate"
                    name="driving"
                    :value="2"
                  />
                  <label for="radioDrivingIntermediate">Intermediate</label>
                </div>
                <div>
                  <input
                    v-model="experiencedDriver"
                    type="radio"
                    id="radioDrivingAdvanced"
                    name="driving"
                    :value="3"
                  />
                  <label for="radioDrivingAdvanced">Advanced</label>
                </div>
              </div>
            </div>

            <!-- swimmer -->
            <div class="swim mb-4 w-full border-b border-purple-ap-t03 pb-5">
              <h3>
                Swimming Skills
              </h3>
              <div class="radio-toolbar wrapped">
                <div>
                  <input
                    v-model="swimmer"
                    type="radio"
                    id="radioSwimAll"
                    name="swim"
                    value="all"
                    checked
                  />
                  <label for="radioSwimAll">Show all</label>
                </div>

                <div>
                  <input
                    v-model="swimmer"
                    type="radio"
                    id="radioSwimBeginner"
                    name="swim"
                    :value="2"
                  />
                  <label for="radioSwimBeginner">Beginner</label>
                </div>

                <div>
                  <input
                    v-model="swimmer"
                    type="radio"
                    id="radioSwimIntermediate"
                    name="swim"
                    :value="3"
                  />
                  <label for="radioSwimIntermediate">Intermediate</label>
                </div>

                <div>
                  <input
                    v-model="swimmer"
                    type="radio"
                    id="radioSwimAdvanced"
                    name="swim"
                    :value="4"
                  />
                  <label for="radioSwimAdvanced">Advanced</label>
                </div>
              </div>
            </div>

            <!-- gender -->
            <div class="gender mb-4 w-full border-b border-purple-ap-t03 pb-5">
              <h3>
                Gender
              </h3>
              <div class="radio-toolbar">
                <input
                  v-model="gender"
                  type="radio"
                  id="radioGenderAll"
                  name="gender"
                  value="any"
                />
                <label for="radioGenderAll">Any</label>

                <input
                  v-model="gender"
                  type="radio"
                  id="radioGenderMale"
                  name="gender"
                  value="M"
                />
                <label for="radioGenderMale">Male</label>

                <input
                  v-model="gender"
                  type="radio"
                  id="radioGenderFemale"
                  name="gender"
                  value="F"
                />
                <label for="radioGenderFemale">Female</label>
              </div>
            </div>

            <!-- in coutnry -->
            <!-- <div class="in-country w-full border-purple-ap-t03 pb-5">
              <h3>
                In-country Au Pairs
              </h3>
              <p class="mb-3">
                <small>Get up to 10% OFF for in-country Au Pairs</small>
              </p>
              <div class="radio-toolbar">
                <input
                  v-model="inCountry"
                  type="radio"
                  id="radioInCountryAll"
                  name="inCountry"
                  value="all"
                />
                <label for="radioInCountryAll">Show All</label>

                <input
                  v-model="inCountry"
                  type="radio"
                  id="radioInCountryYes"
                  name="inCountry"
                  :value="true"
                />
                <label for="radioInCountryYes">Yes</label>
              </div>
            </div> -->
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="this.$store.state.login && isFilterOpen"
      class="overlay"
      @click="closeOverlay"
    ></div>
  </section>
</template>

<script>
import { api } from "@/services.js";
import { mapFields } from "@/helpers.js";

export default {
  name: "AuPairFilters",
  data() {
    return {
      availableCountries: null,
      isFilterOpen: false,
    };
  },
  created() {
    this.$store.dispatch("loadFilter");
    api.get("/promotional/countries").then((r) => {
      this.availableCountries = r.data;
    });
  },
  computed: {
    ...mapFields({
      fields: [
        "name",
        "birthCountry",
        "ageFrom",
        "ageTo",
        "experiencedDriver",
        "infantCareQualified",
        "swimmer",
        "gender",
        "inCountry",
      ],
      base: "filters",
      mutation: "UPDATE_FILTER",
    }),
  },
  watch: {
    name() {
      this.applyFilters();
    },
    birthCountry() {
      this.applyFilters();
    },
    ageFrom() {
      this.applyFilters();
    },
    ageTo() {
      this.applyFilters();
    },
    experiencedDriver() {
      this.applyFilters();
    },
    infantCareQualified() {
      this.applyFilters();
    },
    swimmer() {
      this.applyFilters();
    },
    gender() {
      this.applyFilters();
    },
    // inCountry() {
    //   this.applyFilters();
    // },
  },
  methods: {
    adjustFrom() {
      document.querySelector("#ageFrom").setAttribute("max", this.ageTo);
    },
    adjustTo() {
      document.querySelector("#ageTo").setAttribute("min", this.ageFrom);
    },
    applyFilters() {
      this.$store.dispatch("updateFilter", this.$store.state.filters);
      this.$router.push({ query: { page: "1" } }).catch(() => {});
      this.$emit("applyFilters");
    },
    clearFilter() {
      this.$store.dispatch("clearFilter");
      this.$emit("clearFilter");
    },
    toggleFilters() {
      this.isFilterOpen = !this.isFilterOpen;
    },
    closeOverlay() {
      this.isFilterOpen = false;
    },
  },
};
</script>

<style scoped>
h3 {
  @apply mb-2;
  font-weight: 600;
  font-size: 1rem;
}

.overlay {
  background: rgba(0, 0, 0, 0.7);
  height: 100vh;
  width: 100vw;
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 9;
  transition: all 0.3s;
}

button.filter {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  margin-top: 10px;
  margin-bottom: 20px;
  cursor: pointer;
}

button.filter i {
  margin-left: -10px;
}

.apply {
  background: #fff;
  border: 1px solid rgba(154, 105, 203, 0.6);
  padding: 10px 30px;
  transition: all 0.3s;
  font-weight: 600;
  border-radius: 2px;
  color: #9a69cb;
  @apply rounded-full;
}

.apply:hover {
  background: #9a69cb;
  border: 1px solid rgba(154, 105, 203, 0.6);
  padding: 10px 30px;
  transform: scale(1.05);
  color: #fff;
  transition: all 0.3s;
}

.slider {
  -webkit-appearance: none;
  width: 100%;
  height: 5px;
  border-radius: 5px;
  background: #d3d3d3;
  outline: none;
  opacity: 0.7;
  -webkit-transition: 0.2s;
  transition: opacity 0.2s;
}

.slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #9a69cb;
  cursor: pointer;
}

.slider::-moz-range-thumb {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #9a69cb;
  cursor: pointer;
}

.radio-toolbar.wrapped {
  display: flex;
  flex-wrap: wrap;
}

.radio-toolbar > div {
  width: 49%;
  flex-grow: 1;
}

.radio-toolbar input[type="radio"] {
  opacity: 0;
  position: fixed;
  width: 0;
}

.radio-toolbar label {
  display: inline-block;

  color: #555;
  background-color: #fff;
  font-size: 0.8rem;
  padding: 8px 20px;
  border-radius: 2px;
  border: 1px solid rgba(100, 100, 100, 0.1);
  cursor: pointer;
  transition: all 0.3s;
}

.radio-toolbar.wrapped label {
  width: 100%;
  text-align: center;
}

.radio-toolbar input[type="radio"]:checked + label {
  background-color: #9a69cb;
  border-color: transparent;
  color: #fff;
}

.radio-toolbar input[type="radio"]:focus + label {
  border-color: transparent;
}

.radio-toolbar label:hover {
  background-color: rgba(154, 105, 203, 0.6);
  border-color: transparent;
  color: #fff;
  transition: all 0.3s;
}

/* side filtering*/

#side-filters {
  padding: 0 30px;
  position: fixed;
  right: -100px;
  top: 0;
  width: 0;
  background: #fff;
  height: 100%;
  z-index: 10;
  box-shadow: 2px 4px 8px rgba(0, 0, 0, 0.2);
  overflow-x: hidden;
  transition: width 0.3s, opacity 0.6s;
}

.wrapper {
  position: relative;
  padding: 30px 0;
}

#side-filters.opened {
  width: 400px;
  right: 0;
  opacity: 1;
}

#side-filters.closed {
  width: 0;
  opacity: 0;
  transition: width 0.3s, opacity 0.1s;
}

.close-filter {
  position: absolute;
  top: 10px;
  right: 10px;
}

.close-filter i {
  font-size: 3rem;
  opacity: 0.7;
}

#side-filters li {
  padding: 10px 15px;
  border-bottom: 1px solid #00000010;
}

#side-filters li:last-child {
  border-bottom: none;
}

#side-filters li a {
  color: #555;
}

#side-filters li.list-header {
  padding: 10px 15px;
  @apply text-purple-ap-100;
  text-transform: uppercase;
  font-weight: 600;
  font-size: 0.9rem;
  border: none;
  margin-top: 20px;
}

#side-filters li a.router-link-exact-active {
  @apply text-purple-ap-100;
}

.toggle-menu {
  cursor: pointer;
}

.toggle-menu:hover {
  cursor: pointer;
}

.filter-display span {
  display: inline;
  padding: 10px 15px 0 0;
  font-size: 0.9rem;
}

.filter-display span em {
  font-style: normal;
  text-transform: uppercase;
  color: #00000070;
  font-size: 0.85rem;
}
</style>
